import { useMutation } from '@tanstack/react-query';
import User from 'classes/User';
import type { IUserData } from 'v2/interfaces/user';
import { apiServiceProxy } from 'v2/services/api-proxy';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

type UserStore = {
  user: User;
  originalUser: User;
  isLoading: boolean;
  readonly setUser: (user: User) => void;
  readonly setOriginalUser: (originalUser: User) => void;
  readonly setIsLoading: (isLoading: boolean) => void;
};

export const userStore = create<UserStore>((set: (state: Partial<UserStore>) => void) => ({
  user: null,
  originalUser: null,
  isLoading: false,
  setUser: (u: User) => set({ user: u }),
  setOriginalUser: (originalUser: User) => set({ originalUser }),
  setIsLoading: (isLoading: boolean) => set({ isLoading })
}));

export const useUserStore = <T = UserStore>(selector: (state: UserStore) => T = state => state as T) =>
  userStore(useShallow(selector));

export const useUserUpdate = () => {
  const reloadUserMutation = useMutation({
    mutationFn: async () => {
      const storage = userStore.getState();
      storage.setIsLoading(true);
      const data = await apiServiceProxy.get<IUserData>('/auth/details', null, { cache: false });
      storage.setIsLoading(false);
      const curretUser = storage.user;
      const currentOriginalUser = storage.originalUser;
      curretUser.update(data.data);
      currentOriginalUser.update(data.data);

      storage.setUser(new User(data.data));
      storage.setOriginalUser(new User(data.data));
    }
  });

  return {
    updateUser: reloadUserMutation.mutateAsync,
    isUpdating: reloadUserMutation.isPending
  };
};
